import {city} from "ca-plateform-delivery-api-domain";
import {buildTourApiUrl} from "api/configuration";
import {http} from "api/http";

// Type exports from domain
export interface DeliveryCsvAnalysisQuery
  extends city.colisactiv.delivery.api.model.DeliveryCsvAnalysisQuery {}
export interface DeliveryCsvAnalysisQueryResult
  extends city.colisactiv.delivery.api.model.DeliveryCsvAnalysisQueryResult {}
export interface DeliveryCsvAnalysisAggregatedResult
  extends city.colisactiv.delivery.api.model.DeliveryCsvAnalysisAggregatedResult {}
export interface DeliveryCsvAnalysisSummary
  extends city.colisactiv.delivery.api.model.DeliveryCsvAnalysisSummary {}
export interface DeliveryCsvAnalysisDetail
  extends city.colisactiv.delivery.api.model.DeliveryCsvAnalysisDetail {}
export interface DeliveryCsvScannerResult
  extends city.colisactiv.delivery.api.model.DeliveryCsvScannerResult {}
export interface DeliveryCsvBonusOverview
  extends city.colisactiv.delivery.api.model.DeliveryCsvBonusOverview {}
export interface DeliveryCsvLineError
  extends city.colisactiv.delivery.api.model.DeliveryCsvLineError {}
export interface ReanalysisProgress
  extends city.colisactiv.delivery.api.model.ReanalysisProgress {}
export interface TriggerReanalysisRequest
  extends city.colisactiv.delivery.api.model.TriggerReanalysisRequest {}
export interface HeatmapPoint
  extends city.colisactiv.delivery.api.model.HeatmapPoint {}
export interface HeatmapPointsData
  extends city.colisactiv.delivery.api.model.HeatmapPointsData {}
export interface HeatMatrixCell
  extends city.colisactiv.delivery.api.model.HeatMatrixCell {}
export interface HeatMatricesData
  extends city.colisactiv.delivery.api.model.HeatMatricesData {}
// Status constants (Kotlin enums don't export well to JS)
export const DeliveryCsvAnalysisQueryStatus = {
  READY: "READY",
  PENDING_REANALYSIS: "PENDING_REANALYSIS",
  REANALYSIS_IN_PROGRESS: "REANALYSIS_IN_PROGRESS",
} as const;
export type DeliveryCsvAnalysisQueryStatusType = keyof typeof DeliveryCsvAnalysisQueryStatus;

export const ReanalysisJobStatus = {
  PENDING: "PENDING",
  RUNNING: "RUNNING",
  COMPLETED: "COMPLETED",
  FAILED: "FAILED",
  CANCELLED: "CANCELLED",
} as const;
export type ReanalysisJobStatusType = keyof typeof ReanalysisJobStatus;

export const DeliveryCsvAnalysisSource = {
  API: "API",
  UI: "UI",
} as const;
export type DeliveryCsvAnalysisSourceType = keyof typeof DeliveryCsvAnalysisSource;

const BASE_URL = "/delivery-csv-analysis";

/**
 * Query analyses with filters.
 * Returns status indicating if results are ready or if re-analysis is needed.
 */
const queryAnalyses = (
  query: DeliveryCsvAnalysisQuery
): Promise<DeliveryCsvAnalysisQueryResult> => {
  const url = buildTourApiUrl(`${BASE_URL}/query`);
  return http.post(url, JSON.stringify(query));
};

/**
 * Trigger re-analysis of files matching the query filters.
 * Returns the created job with progress info.
 */
const triggerReanalysis = (
  query: DeliveryCsvAnalysisQuery
): Promise<ReanalysisProgress> => {
  const url = buildTourApiUrl(`${BASE_URL}/trigger-reanalysis`);
  const request: TriggerReanalysisRequest = { query };
  return http.post(url, JSON.stringify(request));
};

/**
 * Get progress of a re-analysis job.
 */
const getJobProgress = (jobId: string): Promise<ReanalysisProgress | null> => {
  const url = buildTourApiUrl(`${BASE_URL}/reanalysis-job/${jobId}`);
  return http.get(url, "Erreur lors de la récupération du job");
};

/**
 * Get all jobs (active + recently completed).
 */
const getJobs = (): Promise<ReanalysisProgress[]> => {
  const url = buildTourApiUrl(`${BASE_URL}/jobs`);
  return http.get(url, "Erreur lors de la récupération des jobs");
};

/**
 * Cancel a running or pending job.
 */
const cancelJob = (jobId: string): Promise<void> => {
  const url = buildTourApiUrl(`${BASE_URL}/reanalysis-job/${jobId}`);
  return http.delete(url, "Job annulé", true);
};

/**
 * Get detailed analysis for a single file.
 * Includes full bonus overviews and errors.
 * When content filters are provided, points and bonus overviews are filtered accordingly.
 */
const getAnalysisDetail = (
  analysisId: string,
  filters?: {
    operatorIds?: string[];
    carrierIds?: string[];
    territoryCodes?: string[];
    contentPeriodStart?: string;
    contentPeriodEnd?: string;
  }
): Promise<DeliveryCsvAnalysisDetail | null> => {
  const params = new URLSearchParams();
  filters?.operatorIds?.forEach((id) => params.append("operatorIds", id));
  filters?.carrierIds?.forEach((id) => params.append("carrierIds", id));
  filters?.territoryCodes?.forEach((code) => params.append("territoryCodes", code));
  if (filters?.contentPeriodStart) params.append("contentPeriodStart", filters.contentPeriodStart);
  if (filters?.contentPeriodEnd) params.append("contentPeriodEnd", filters.contentPeriodEnd);
  const queryString = params.toString();
  const url = buildTourApiUrl(`${BASE_URL}/${analysisId}${queryString ? `?${queryString}` : ""}`);
  return http.get(url, "Erreur lors de la récupération des détails de l'analyse");
};

/**
 * Trigger CSV export of delivery points matching the query filters.
 * The export is processed in the background and sent by email.
 */
const triggerCsvExport = (
  query: DeliveryCsvAnalysisQuery
): Promise<void> => {
  const url = buildTourApiUrl(`${BASE_URL}/export-csv`);
  return http.post(url, JSON.stringify(query));
};

export const deliveryCsvAnalysisClient = {
  queryAnalyses,
  triggerReanalysis,
  getJobProgress,
  getJobs,
  cancelJob,
  getAnalysisDetail,
  triggerCsvExport,
};
